/** Grace period after a competition end date before it is hidden from public portals. */
export const PUBLIC_COMPETITION_GRACE_DAYS = 14;

const FINISHED_STATUSES = new Set([
  'completed',
  'finished',
  'cancelled',
  'archived',
  'ended',
]);

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Public visibility cutoff = endDate + grace days (end of that calendar day).
 * Returns null if there is no usable end date.
 */
export const getPublicVisibilityCutoff = (
  competition,
  { graceDays = PUBLIC_COMPETITION_GRACE_DAYS } = {}
) => {
  if (!competition) return null;
  const endDate = parseDate(competition.endDate ?? competition.end_date);
  if (!endDate) return null;
  const cutoff = startOfDay(endDate);
  cutoff.setDate(cutoff.getDate() + graceDays);
  cutoff.setHours(23, 59, 59, 999);
  return cutoff;
};

/**
 * Whether a league/tournament should appear on public portal pages.
 * Hidden when finished and more than `graceDays` have passed since endDate.
 * Admin dashboards should continue using unfiltered service calls.
 */
export const isCompetitionPubliclyVisible = (
  competition,
  { graceDays = PUBLIC_COMPETITION_GRACE_DAYS, now = new Date() } = {}
) => {
  if (!competition) return false;

  const status = String(
    competition.leagueStatus ??
      competition.tournamentStatus ??
      competition.status ??
      ''
  ).toLowerCase();

  const cutoff = getPublicVisibilityCutoff(competition, { graceDays });
  if (cutoff) {
    return now.getTime() <= cutoff.getTime();
  }

  // No end date: keep upcoming/active visible; hide clearly finished statuses
  if (FINISHED_STATUSES.has(status)) {
    return false;
  }

  return true;
};

export const filterPublicCompetitions = (
  competitions = [],
  options = {}
) => (competitions || []).filter((item) => isCompetitionPubliclyVisible(item, options));
